import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, Copy, Share2, UserMinus, Loader2, KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function FamilySharing() {
  const { activeId, activeHousehold, loadHouseholds, selectHousehold } = useAuth();
  const [members, setMembers] = useState(null);
  const [invite, setInvite] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.get(`/households/${activeId}/members`); setMembers(data); } catch { /* optional */ }
  }, [activeId]);
  useEffect(() => { setInvite(null); load(); }, [load]);

  const makeInvite = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/households/${activeId}/invite`); setInvite(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const shareText = invite ? `Join our household "${invite.household_name}" on Sorted 🍝 Open ${window.location.origin}/profile, tap "Join a household" and enter code ${invite.code}` : "";
  const copy = async () => { await navigator.clipboard?.writeText(shareText); toast.success("Invite copied — send it to your partner"); };
  const share = async () => { if (navigator.share) { try { await navigator.share({ title: "Join our Sorted household", text: shareText }); } catch { /* cancelled */ } } else copy(); };
  const join = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/households/join", { code: code.trim() });
      await loadHouseholds(); selectHousehold(data.id); setCode("");
      toast.success(`You're in! Welcome to ${data.name} 🎉`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (p) => {
    try { await api.post(`/households/${activeId}/members/remove`, { user_id: p.id }); toast(p.you ? "You've left this household" : `${p.name} removed`); await loadHouseholds(); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-white rounded-3xl border-2 border-[#FFF3F0] shadow-soft p-4" data-testid="family-sharing">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-[#FCEFCF] flex items-center justify-center text-amber-500"><Users size={18} strokeWidth={2.5} /></span>
        <div className="flex-1">
          <p className="font-heading font-semibold text-sorted-ink">Family sharing</p>
          <p className="text-xs text-sorted-slate">Your partner sees the same plan, list, kitchen and cook mode.</p>
        </div>
      </div>

      {members && (
        <div className="mt-4 space-y-2" data-testid="family-members">
          {members.people.map((p) => (
            <div key={p.id} className="flex items-center gap-3 bg-muted/60 rounded-2xl px-3 py-2" data-testid={`family-member-${p.id}`}>
              {p.picture ? <img src={p.picture} alt="" className="w-8 h-8 rounded-full" /> : <span className="w-8 h-8 rounded-full bg-sorted-blush flex items-center justify-center font-bold text-sorted-ink text-sm">{(p.name || p.email)[0]?.toUpperCase()}</span>}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-sorted-ink truncate">{p.name || p.email}{p.you ? " (you)" : ""}</p>
                <p className="text-[11px] text-sorted-slate capitalize">{p.role}</p>
              </div>
              {p.role !== "owner" && (members.is_owner || p.you) && (
                <button onClick={() => remove(p)} aria-label="Remove" data-testid={`family-remove-${p.id}`} className="w-8 h-8 rounded-full bg-white text-sorted-slate flex items-center justify-center"><UserMinus size={14} strokeWidth={2.5} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        {invite ? (
          <div className="bg-[#FFF6F1] rounded-2xl p-3" data-testid="invite-box">
            <p className="text-xs font-bold text-sorted-slate">Invite code · valid {invite.expires_in_days} days</p>
            <p className="font-heading text-3xl font-bold tracking-[0.3em] text-sorted-ink mt-1" data-testid="invite-code">{invite.code}</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button onClick={copy} data-testid="invite-copy" className="bg-white border-2 border-[#FFF3F0] text-sorted-ink text-xs font-bold py-2.5 rounded-full flex items-center justify-center gap-1"><Copy size={13} strokeWidth={2.5} /> Copy</button>
              <button onClick={share} data-testid="invite-share" className="bg-sorted-rose text-white text-xs font-bold py-2.5 rounded-full flex items-center justify-center gap-1"><Share2 size={13} strokeWidth={2.5} /> Share</button>
            </div>
          </div>
        ) : (
          <button onClick={makeInvite} disabled={busy} data-testid="invite-create" className="w-full bg-sorted-peach text-sorted-ink font-bold py-3 rounded-full flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} strokeWidth={2.5} />} Invite {activeHousehold?.name ? `to ${activeHousehold.name}` : "your partner"}
          </button>
        )}
      </div>

      <div className="mt-4">
        <p className="text-xs font-bold text-sorted-slate mb-1.5 flex items-center gap-1"><KeyRound size={12} strokeWidth={2.5} /> Got a code? Join a household</p>
        <div className="flex items-center gap-2">
          <input data-testid="join-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. Q7UA9B" maxLength={6}
            className="flex-1 min-w-0 bg-muted rounded-2xl px-3 py-2.5 text-sm font-bold tracking-widest text-sorted-ink outline-none focus:ring-2 focus:ring-sorted-peach uppercase" />
          <button onClick={join} disabled={busy || code.length < 6} data-testid="join-code-btn" className="bg-sorted-rose disabled:opacity-40 text-white text-xs font-bold px-4 py-2.5 rounded-full">Join</button>
        </div>
      </div>
    </div>
  );
}
